import styled from 'styled-components';

export const ColumnContainer = styled.div`
    background: #101204;
    border-radius: 10px;
    width: ${props => props.isCollapsed ? '42px' : '272px'};
    min-height: 100px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease;
    max-height: 100%;

    @media (max-width: 768px) {
        width: ${props => props.isCollapsed ? '42px' : 'calc(100% - 16px)'};
    }
`;

export const ColumnHeader = styled.div`
    display: flex;
    align-items: center;
    min-height: 32px;
    gap: 8px;
    
    ${props => props.isCollapsed && `
        flex-direction: column;
        height: 100%;
        padding: 12px 4px;
        margin: 0;
    `}

    .card-count-badge {
        margin-left: 8px;
        padding: 2px 6px;
        background-color: #2a2c20; /* Slightly darker than column bg */
        color: #aaa; /* Lighter than title for contrast */
        border-radius: 5px;
        font-size: 12px;
        font-weight: normal;
        align-self: center; /* To vertically align with the input field */
    }
`;

// Specific styling for the badge when inside VerticalTitle might be needed if the above doesn't cover it.
// However, VerticalTitle itself is a div, and the span is inside it.
// The writing-mode of VerticalTitle might affect the margin-left.
// Let's test this first. If `margin-left` doesn't work as expected in vertical mode,
// we might need to target it specifically, e.g., within VerticalTitle context.

export const HeaderControls = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
`;

export const CollapseButton = styled.button`
    background: transparent;
    border: none;
    color: #bbb;
    cursor: pointer;
    padding: 4px;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
    }
`;

export const ColumnTitle = styled.input`
    font-weight: bold;
    font-size: 14px;
    background: transparent;
    border: none;
    padding: 4px 8px;
    border-radius: 3px;
    flex: 1;
    min-width: 0;
    color: #ccc; // Ensure text color is always set
    
    &:focus {
        background-color: transparent !important; // Added !important
        outline: 2px solid #0079bf;
        color: #ccc; // Ensure text color on focus
    }

    &:hover {
        // When editable (not readOnly), use a subtle background on hover, not white.
        // If it's readOnly, use the existing hover.
        background: ${props => props.readOnly ? 'rgba(9, 30, 66, 0.08)' : 'rgba(255, 255, 255, 0.05)'};
        cursor: ${props => props.readOnly ? 'pointer' : 'text'};
    }
`;

export const VerticalTitle = styled.div`
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    color: #ccc;
    font-weight: bold;
    font-size: 14px;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-height: 200px;
    overflow: hidden;
    margin: auto 0;

    .card-count-badge {
        display: inline-block; // Needed for vertical layout to respect margins properly
        margin-left: 0; // Reset margin-left from horizontal
        margin-top: 8px; // Space it out in vertical flow
        writing-mode: horizontal-tb; // Reset writing mode for the badge itself
        transform: rotate(180deg); // Changed from none
        // background-color, color, padding, border-radius, font-size will be inherited
        // or can be re-specified if needed for a different look in vertical mode.
        // For now, assume they are fine.
    }
`;

export const DragHandle = styled.div`
    padding: 6px;
    cursor: grab;
    display: flex;
    align-items: center;
    color: #bbb;
    flex-shrink: 0;
`;

export const CardList = styled.div`
    padding: 4px;
    flex-grow: 1;
    background-color: inherit;
    transition: background-color 0.2s ease;
    border-radius: 3px;
    overflow-y: ${props => props.limitColumnHeight ? 'auto' : 'visible'};
    overflow-x: hidden;
    
    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 4px;
        
        &:hover {
            background: rgba(255, 255, 255, 0.3);
        }
    }
`;

export const AddCardButton = styled.button`
  width: 100%;
  padding: 8px;
  background: transparent;
  border: none;
  color: #bbb;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  border-radius: 3px;

  &:hover {
    background: rgba(9, 30, 66, 0.08);
    color: #fff;
  }

  &:active {
    background: rgba(9, 30, 66, 0.13);
  }
`;

export const AddCardForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px; /* Space between input and button */
  margin-top: 8px; /* Space above the form */
`;

export const AddCardInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  border-radius: 3px;
  border: 1px solid #4A4E41; /* A subtle border that fits the dark theme */
  box-sizing: border-box; /* Ensure padding doesn't increase width */
  background-color: #2A2E21; /* Slightly lighter than column background for better visibility, but still dark */
  color: #E0E0E0; /* Slightly lighter than #ccc for better readability */

  &:focus {
    outline: none;
    border-color: #6A8EAE; /* a less bright blue than Trello's default */
    box-shadow: 0 0 0 2px rgba(106, 142, 174, 0.3);
  }

  &::placeholder {
    color: #8A8E81;
  }
`;

export const AddCardControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px; /* spacing between input and button */
  width: 100%;
`;

export const SaveCardButton = styled.button`
  background: transparent;
  color: #bbb; /* for the icon color */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px; /* adjust as needed for icon size */
  border-radius: 3px;
  border: none;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }

  &:active {
    background: rgba(255, 255, 255, 0.15);
  }
`;
